import { Center, Loader } from '@mantine/core';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { RobotInfo } from '@mygpt/app/apis';
import { searchNodeInfoByIds } from '@mygpt/app/faqs';
import { WebSocketClient } from '@mygpt/app/socket';
import { PageInfo, SessionListParam } from '@mygpt/types';

import { I_UserFormValues } from '@/components/WidgetSetting/form-context';
import { KEY_MYGPT_TOKEN, MIXPANEL_TOKEN, WIDGET_FOOTER_DEFAULT_TEXT } from '@/constant';
import { useSessionId } from '@/hooks/useSessionId';
import { useGPTStore } from '@/store';
import { DatasetsInfo } from '@/utils/interface';
import { useGetLanguageInfo } from '@/hooks/useGetLanguageInfo';

import ChatView, { I_configs } from './ChatView';
import { getBrowserFingerprint } from '@mygpt/app/utils/fingerprint';

export default () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { userInfo } = useGPTStore((state) => state);
  const { refreshId, sessionId } = useSessionId(id);
  const { voiceLanguage } = useGetLanguageInfo();

  const [frozenTipOpened, setFrozenTipOpened] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [configs, setConfigs] = useState<I_configs>();
  const [robotInfo, setRobotInfo] = useState<RobotInfo>();
  const [hideCloseButton, setHideCloseButton] = useState<boolean>(false);
  const [noWidgetFooter, setNoWidgetFooter] = useState<boolean>(false);

  const pageInfoRef = useRef<PageInfo>({ url: '', title: '' });
  const webSocketClientRef = useRef<WebSocketClient | null>(null);

  const resetPageStyle = () => {
    document.querySelector('html')?.classList.add('h-full');
    document.querySelector('body')?.classList.add('h-full');
    const rootElement = document.querySelector('#root') as HTMLElement;
    rootElement && (rootElement.style.height = '100vh');
    rootElement && (rootElement.style.overflow = 'hidden');
  };

  const onClose = function () {
    window.parent.postMessage({ closeIframe: true }, '*');
    closeClientSocket();
  };

  // 初始化机器人配置
  const listenConfigs = async (configs: I_UserFormValues, title?: undefined | string) => {
    const { chatbot, chatbotAvatar, digitalHuman } = configs;
    if (!chatbot || !chatbotAvatar) return;
    const signatureMessages =
      chatbot.signatureMessages &&
      chatbot.signatureMessages.checked &&
      ['all', 'widget'].includes(chatbot.signatureMessages.displayType)
        ? {
            value: chatbot.signatureMessages.value,
            list: chatbot.signatureMessages.list,
            checked: chatbot.signatureMessages.checked,
            displayType: chatbot.signatureMessages.displayType,
          }
        : undefined;
    const welcomeMessages = chatbot.welcomeMessage.checked
      ? chatbot.welcomeMessage.type && chatbot.welcomeMessage.type === 'multiLanguage'
        ? {
            zh: chatbot.welcomeMessage.multiLanguageValues.zh?.split(/[\n]/) || [],
            en: chatbot.welcomeMessage.multiLanguageValues.en?.split(/[\n]/) || [],
            ja: chatbot.welcomeMessage.multiLanguageValues.ja?.split(/[\n]/) || [],
            ko: chatbot.welcomeMessage.multiLanguageValues.ko?.split(/[\n]/) || [],
          }
        : chatbot.welcomeMessage.value.split(/[\n]/)
      : [];

    let suggestedMessages = [];
    if (chatbot.suggestedMessages.checked && chatbot.suggestedMessages.type === 'tree' && id) {
      if (chatbot.suggestedMessages.node_list.length > 0) {
        const { data: treeMessages } = await searchNodeInfoByIds({
          ai_id: id,
          ids: chatbot.suggestedMessages.node_list,
        });
        suggestedMessages = treeMessages;
      } else {
        suggestedMessages = [];
      }
    } else {
      suggestedMessages =
        chatbot.suggestedMessages.checked && chatbot.suggestedMessages.value
          ? chatbot.suggestedMessages.value.split(/[\n]/)
          : [];
    }
    const inputPlaceholder = chatbot.inputPlaceholder.checked ? chatbot.inputPlaceholder.value : '';
    const chatbotWidgetColor = chatbot.widgetColor;
    const widgetColor = {
      chatColor: chatbotWidgetColor.chatColor.checked ? chatbotWidgetColor.chatColor.value : '',
      sendButtonColor: chatbotWidgetColor.sendButtonColor.checked ? chatbotWidgetColor.sendButtonColor.value : '',
      userMessageColor: chatbotWidgetColor.userMessageColor.checked ? chatbotWidgetColor.userMessageColor.value : '',
      botMessageColor: chatbotWidgetColor.botMessageColor.checked ? chatbotWidgetColor.botMessageColor.value : '',
    };
    const { checked: footerBrandChecked, value: footerBrandValue } = chatbot.footerBrand;
    const footerBrand = footerBrandChecked && footerBrandValue !== WIDGET_FOOTER_DEFAULT_TEXT ? footerBrandValue : '';

    setTitle(chatbot.chatHeaderDisplay.checked ? chatbot.chatHeaderDisplay.value : title || '');
    let initMessages: SessionListParam[] = [];
    if (welcomeMessages && (Array.isArray(welcomeMessages) || Object.keys(welcomeMessages).length)) {
      if (Array.isArray(welcomeMessages)) {
        // 单语言模式
        initMessages = welcomeMessages.map((item: string) => ({
          isAnswer: true,
          text: item,
        }));
      } else {
        // 多语言模式，根据当前语言选择对应的欢迎语
        const currentLang = voiceLanguage as keyof typeof welcomeMessages;
        const messages = welcomeMessages[currentLang] || '';
        initMessages = messages.map((item: string) => ({
          isAnswer: true,
          text: item,
        }));
      }
    }
    if (!initMessages.length && suggestedMessages.length) {
      // 当没有欢迎语且有推荐问题的时候，手动加上提示语句
      initMessages.push({
        isAnswer: true,
        text: t('widget.setting.chatbot.suggested_greeting_message'),
      });
    }

    // 数字人背景色默认配置
    const digitalHumanDefault = {
      backgroundColor: {
        checked: true,
        value: '#c5c9ee',
      },
      playWelcomeMessage: {
        enabled: true,
        mode: 'first',
      },
    };

    setConfigs({
      welcomeMessages,
      signatureMessages,
      suggestedMessages,
      inputPlaceholder,
      footerBrand,
      widgetColor,
      chatbotAvatar,
      answerSource: chatbot.answerSource,
      initMessages,
      digitalHuman: digitalHuman || digitalHumanDefault,
    });
  };

  const listenPageInfo = (pageInfo: PageInfo) => {
    pageInfoRef.current = { ...pageInfo, ...{ source: 'widget' } };
  };

  const listenError = (messageEvent: MessageEvent) => {
    console.error('post message error: ', messageEvent);
    Sentry.captureMessage(`post message error: ${JSON.stringify(messageEvent.data)}}`, 'error');
  };

  const listen = (messageEvent: MessageEvent) => {
    const {
      closeChat,
      configs,
      datasets,
      mixpanelKey,
      mixpanelValue,
      openChat,
      pageInfo,
      robotInfo,
      title,
      customizedData,
      hideCloseButton,
      noWidgetFooter,
    } = messageEvent.data;
    console.log('messageEvent', messageEvent.data);
    if (robotInfo) setRobotInfo(robotInfo);
    if (configs) listenConfigs(configs, title);
    if (pageInfo) listenPageInfo(pageInfo);
    if (datasets) {
      setFrozenTipOpened(datasets.some((dataset: DatasetsInfo) => dataset.data_status === 'frozen'));
    }

    if (openChat) initClientSocket();
    if (closeChat) closeClientSocket();
    if (mixpanelKey) {
      try {
        mixpanelKey === 'register' ? mixpanel.register(mixpanelValue) : mixpanel.track(mixpanelKey, mixpanelValue);
      } catch (error) {
        console.log('mixpanel error', error);
      }
    }
    if (customizedData) {
      // 如果接收到了 HIS Mobile 塞在页面上的 phone_list 数据，那么就塞到 window 上，供 PhoneList 组件使用。
      if (customizedData.hismobile_phone_list) {
        window.hismobile_phone_list = customizedData.hismobile_phone_list;
      }
    }
    if (hideCloseButton) setHideCloseButton(hideCloseButton);
    if (noWidgetFooter) setNoWidgetFooter(noWidgetFooter);
  };

  const initClientSocket = () => {
    const pageUrl = pageInfoRef.current.url;
    const token = (Cookies.get(KEY_MYGPT_TOKEN) || '').replace('Bearer ', '');
    // 创建 WebSocketClient 实例
    const webSocketClient = new WebSocketClient({
      url: `wss:${new URL(import.meta.env.VITE_API_BASE_URL).host}/notification/client/ws${
        token ? `?token=${token}` : ''
      }`,
      reconnectInterval: 5000, // 5秒重连间隔
      maxReconnectAttempts: 10, // 最大重连尝试次数
      connectionTimeout: 10000, // 10秒连接超时
    });

    // 注册事件监听器
    webSocketClient.on('open', () => {
      webSocketClient.send({
        type: 'request',
        cmd: 'login_req',
        data: {
          ai_id: id,
          user_id: userInfo?.user_id || `Visitor ${new Date().getTime() + pageUrl.length}`,
          page_url: pageUrl,
          session_id: sessionId,
          session_type: 'widget',
        },
      });
    });

    webSocketClient.on('message', (message) => {});

    webSocketClient.on('error', (error) => {
      // console.error('WebSocket encountered an error:', error);
    });

    webSocketClient.on('close', (event) => {
      // console.log('WebSocket connection closed:', event);
    });

    // 启动 WebSocket 客户端
    webSocketClient.start();
    webSocketClientRef.current = webSocketClient;

    // 在需要的时候发送消息
    // webSocketClient.send({ type: 'request', cmd: 'some_command', data: {} });

    // 在不需要 WebSocket 连接的时候关闭
    // webSocketClient.stop();
  };

  const closeClientSocket = () => {
    webSocketClientRef.current?.stop();
  };

  useLayoutEffect(() => {
    resetPageStyle();
  }, []);

  // 记录当前网站埋点
  useEffect(() => {
    const parentUrl = searchParams.get('parentUrl');
    if (parentUrl) {
      const localStorageKey = 'MIX_GPTBASE_KEY_WIDGET_URL';
      const value = window.storageSupported ? localStorage.getItem(localStorageKey) : true;
      if (!value) {
        try {
          mixpanel.track('embed_widget_url', {
            ai_id: id,
            url: parentUrl,
          });
          localStorage.setItem(localStorageKey, parentUrl);
        } catch (error) {
          console.log('mixpanel error', error);
        }
      }
    }
    getBrowserFingerprint();
  }, []);

  // post message 通信 from plugin.js
  useLayoutEffect(() => {
    window.addEventListener('message', listen);
    window.addEventListener('messageerror', listenError);
    window.parent.postMessage({ initEnd: true }, '*');

    return () => {
      window.removeEventListener('message', listen);
      window.removeEventListener('messageerror', listenError);
    };
  }, []);

  // 添加 voiceLanguage 变化的处理
  useEffect(() => {
    if (configs?.welcomeMessages) {
      const welcomeMessages = configs.welcomeMessages;
      if (!Array.isArray(welcomeMessages) && Object.keys(welcomeMessages).length) {
        // 多语言模式，根据当前语言选择对应的欢迎语
        const currentLang = voiceLanguage as keyof typeof welcomeMessages;
        const messages = welcomeMessages[currentLang] || [];
        const newInitMessages = messages.map((item: string) => ({
          isAnswer: true,
          text: item,
        }));

        setConfigs((prev) => ({
          ...prev!,
          initMessages: newInitMessages,
        }));
      }
    }
  }, [voiceLanguage]);

  console.log('configs', configs);

  if (!configs) {
    // 检查 URL 参数是否来自 widget-page
    const urlParams = new URLSearchParams(window.location.search);
    const isFromWidgetPage = urlParams.get('from') === 'widget-page';

    return <Center h={'100vh'}>{isFromWidgetPage ? null : <Loader color="gray.3" />}</Center>;
  }

  return (
    <ChatView
      // key={sessionId}
      title={title}
      configs={configs}
      isFrozen={frozenTipOpened}
      sessionInfo={{
        aiId: id || '',
        sessionId,
        pageInfo: pageInfoRef.current,
      }}
      robotInfo={robotInfo}
      onClose={onClose}
      onRefresh={() => {
        refreshId();
      }}
      hideCloseButton={hideCloseButton}
      noWidgetFooter={noWidgetFooter}
    />
  );
};
